import { traegerbestaetigungForm } from '../templates'
import { assertWriteIdentity, captureWriteIdentity, type WriteIdentity } from '@/core/api/http'
import { fillTb } from '../lib/fillTb'
import { ok, technical, type Result, type FieldId } from '@/core/lib/result'
import { validatePresence, presenceRulesFromTemplate } from '@/core/lib/validate'
import { outOfRange, maxEndeError, type FieldError, type FieldErrors } from '@/core/lib/fieldError'
import { parseMaxDauer, formatMaxDauer, maxEndeDatum } from '../lib/maxDauer'
import type { FormValues } from '@/core/components/form/formModel'
import { getMassnahmeTbFields } from '@/core/data/massnahmen'
import { getDocData, getDocBucketAddress, docRowExists, patchDocData, upsertDoc } from '@/core/data/dokumente'
import { uploadFile, removeFile, versionedFile } from '@/core/data/storage'
import { assemblePages } from '@/core/lib/pdfSplit'
import {
  setAufgabe,
  rewindJourney,
  listCoaches,
  type AdvanceResult,
} from '@/core/data/coachings'
import type { AufgabeKey } from '@/core/data/aufgabe'
import { loadErstgespraech } from './erstgespraech'
import { getAt } from '@/core/lib/path'
import { getAssignedCoaches, getTbEinreichung, patchCoaching } from '../data/coaching'
import { isJobcenter } from '../lib/berichtTemplate'
import type { TabOverrides } from '@/core/templates/types'
import { apiFirst, apiPatch } from '@/core/api/resources'

const tbPresenceRules = presenceRulesFromTemplate(traegerbestaetigungForm, {
  optional: ['Maßnahme.Zweiter Coach (optional)'],
})

export const tbManualFields: ReadonlySet<FieldId> = new Set(tbPresenceRules.required)

export const TB_EMPTY = 'bestaetigung-empty'
const TB_FILLED = 'bestaetigung'
const AVGS_DOC = 'avgs'

async function clearCombinedOriginal(coachingId: string, identity: WriteIdentity): Promise<void> {
  try {
    const daten = await getDocData(coachingId, AVGS_DOC)
    assertWriteIdentity(identity)
    const original = daten?.combinedOriginal
    if (typeof original !== 'string' || !daten) return
    const { combinedOriginal: _drop, ...rest } = daten
    await upsertDoc(coachingId, AVGS_DOC, { daten: rest }, identity)
    await removeFile(original, identity).catch(() => { assertWriteIdentity(identity) })
  } catch (e) {
    assertWriteIdentity(identity)
    console.error("Ursprungsdatei konnte nicht bereinigt werden:", e)
  }
}

export async function invalidateFilledTb(
  coachingId: string,
  opts?: { dropFormDraft?: boolean; strict?: boolean },
  identity: WriteIdentity = captureWriteIdentity(),
): Promise<void> {
  try {
    if (opts?.dropFormDraft) await upsertDoc(coachingId, TB_EMPTY, { daten: {} }, identity)
    const exists = await docRowExists(coachingId, TB_FILLED)
    assertWriteIdentity(identity)
    if (!exists) return
    const prevPath = await getDocBucketAddress(coachingId, TB_FILLED)
    if (prevPath) await removeFile(prevPath, identity)
    await upsertDoc(coachingId, TB_FILLED, {
      daten: opts?.dropFormDraft ? {} : undefined,
      bucketAddress: '',
      fileType: '',
      fileSize: 0,
    }, identity)
  } catch (e) {
    assertWriteIdentity(identity)
    console.error("Trägerbestätigung konnte nicht zurückgesetzt werden:", e)
    if (opts?.strict) throw e
  }
}

async function settleBestEffort(coachingId: string, aufgabe: AufgabeKey, identity: WriteIdentity): Promise<void> {
  try {
    const moved = await setAufgabe(coachingId, aufgabe, identity)
    if (!moved.advanced) await rewindJourney(coachingId, aufgabe, 'tbPruefen', identity)
  } catch (e) {
    assertWriteIdentity(identity)
    console.error(`Aufgabe "${aufgabe}" konnte nicht gesetzt werden:`, e)
  }
}

export async function discardBlankTb(coachingId: string, identity: WriteIdentity = captureWriteIdentity()): Promise<void> {
  const blank = await getDocBucketAddress(coachingId, TB_EMPTY)
  assertWriteIdentity(identity)
  if (blank) {
    await removeFile(blank, identity)
    await patchDocData(coachingId, TB_EMPTY, { verworfen: true }, { bucketAddress: '', fileType: '', fileSize: 0 }, identity)
  }
  try {
    const filled = await getDocBucketAddress(coachingId, TB_FILLED)
    assertWriteIdentity(identity)
    if (filled) await invalidateFilledTb(coachingId, undefined, identity)
  } catch (e) {
    assertWriteIdentity(identity)
    console.error("Trägerbestätigung konnte nicht zurückgesetzt werden:", e)
  }
}

export type TbErfassen = { kostentraeger: string; jobcenter: boolean; verworfen: boolean }

export async function loadTbErfassen(coachingId: string): Promise<TbErfassen | null> {
  const [choice, blank] = await Promise.all([getTbEinreichung(coachingId), getDocData(coachingId, TB_EMPTY)])
  if (choice?.einreichung !== 'original') return null
  const jobcenter = isJobcenter(choice.agentur?.behoerde)
  return {
    kostentraeger: [jobcenter ? "Jobcenter" : "Agentur für Arbeit", choice.agentur?.standort].filter(Boolean).join(' '),
    jobcenter,
    verworfen: blank?.verworfen === true,
  }
}

export async function tbTabOverrides(coachingId: string): Promise<TabOverrides> {
  const choice = await getTbEinreichung(coachingId)
  if (choice?.einreichung !== 'original') return {}
  return {
    'tb-hochladen': { hidden: true },
    bestaetigung: { label: "Trägerbestätigung erfassen", icon: 'i-edit' },
  }
}

export async function getTbFilledPath(coachingId: string): Promise<string | null> {
  return getDocBucketAddress(coachingId, TB_FILLED)
}

export type TbCheck = { errors: FieldErrors }

export type GueltigkeitWindow = { beginn: string; ende: string }

const TB_BEGINN: FieldId = 'Maßnahme.Beginn der Teilnahme'
const TB_ENDE: FieldId = 'Maßnahme.Ende der Teilnahme'
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const isPlaceholderWindow = (g: GueltigkeitWindow) =>
  g.beginn === '1970-01-01' && g.ende === '1970-01-01'

export function checkTb(
  values: Record<string, unknown>,
  gueltigkeit?: GueltigkeitWindow | null,
  maxDauerRaw?: string | null,
): TbCheck {
  const { missing } = validatePresence(values, tbPresenceRules)
  const errors = new Map<FieldId, FieldError>()
  for (const path of missing) errors.set(path, { kind: 'missing' })

  const tbM = (values['Maßnahme'] ?? {}) as Record<string, unknown>
  const beginn = String(tbM['Beginn der Teilnahme'] ?? '')

  if (gueltigkeit && !isPlaceholderWindow(gueltigkeit) && !errors.has(TB_BEGINN)) {
    if (
      ISO_DATE_RE.test(beginn) &&
      ISO_DATE_RE.test(gueltigkeit.beginn) &&
      ISO_DATE_RE.test(gueltigkeit.ende) &&
      (beginn < gueltigkeit.beginn || beginn > gueltigkeit.ende)
    ) {
      errors.set(TB_BEGINN, outOfRange("AVGS", gueltigkeit.beginn, gueltigkeit.ende))
    }
  }

  if (maxDauerRaw && !errors.has(TB_ENDE)) {
    const dauer = parseMaxDauer(maxDauerRaw)
    const ende = String(tbM['Ende der Teilnahme'] ?? '')
    if (dauer && ISO_DATE_RE.test(beginn) && ISO_DATE_RE.test(ende)) {
      const maxEnde = maxEndeDatum(beginn, dauer)
      if (maxEnde && ende > maxEnde) {
        errors.set(TB_ENDE, maxEndeError(beginn, maxEnde, formatMaxDauer(dauer)))
      }
    }
  }

  return { errors }
}

export async function applyTbUpload(coachingId: string, file: File, identity: WriteIdentity = captureWriteIdentity()): Promise<Result<void>> {
  const blank = file.type.toLowerCase() === 'application/pdf' ? ok<Blob>(file) : await pageOf(file)
  if (!blank.ok) return blank
  const prevPath = await getDocBucketAddress(coachingId, TB_EMPTY)
  const path = await uploadFile(blank.data, versionedFile('bestaetigung-empty', 'pdf'), identity)
  await upsertDoc(coachingId, TB_EMPTY, {
    bucketAddress: path,
    fileType: 'application/pdf',
    fileSize: blank.data.size,
  }, identity)
  if (prevPath) await removeFile(prevPath, identity).catch(() => { assertWriteIdentity(identity) })
  await clearCombinedOriginal(coachingId, identity)
  await invalidateFilledTb(coachingId, { dropFormDraft: true }, identity)
  await settleBestEffort(coachingId, 'tbAusfuellen', identity)
  return ok(undefined)
}

async function pageOf(image: File): Promise<Result<Blob>> {
  const assembled = await assemblePages([image], { fitA4: true })
  return assembled.ok ? ok(new Blob([new Uint8Array(assembled.data.bytes)], { type: 'application/pdf' })) : assembled
}

export async function loadTbForm(coachingId: string): Promise<Record<string, unknown> | null> {
  const empty = await getDocData(coachingId, TB_EMPTY)
  const filled = empty?.form ? null : await getDocData(coachingId, TB_FILLED)
  const form = ((empty?.form ?? filled?.form) as Record<string, unknown> | undefined) ?? null
  return withCoachFromAssignment(coachingId, await withMassnahmeFromEg(coachingId, form))
}

async function withMassnahmeFromEg(
  coachingId: string,
  form: Record<string, unknown> | null,
): Promise<Record<string, unknown> | null> {
  const tbM = (form?.['Maßnahme'] ?? {}) as Record<string, unknown>
  if (String(tbM['Bezeichnung der Maßnahme'] ?? '').trim()) return form
  const eg = await loadErstgespraech(coachingId)
  const egM = (eg?.['Maßnahme'] ?? {}) as Record<string, unknown>
  const choice = String(egM['Maßnahmennummer'] ?? '').trim()
  if (!choice) return form
  const base = form ?? {}
  return {
    ...base,
    Maßnahme: { ...tbM, 'Bezeichnung der Maßnahme': choice },
  }
}

async function withCoachFromAssignment(
  coachingId: string,
  form: Record<string, unknown> | null,
): Promise<Record<string, unknown> | null> {
  const fields = (form?.['Maßnahme'] ?? {}) as Record<string, unknown>
  const [assigned, catalog] = await Promise.all([getAssignedCoaches(coachingId), listCoaches()])
  const current = String(fields['Coach auswählen'] ?? '')
  const matches = catalog.filter((coach) => coach.id === current || coach.name === current)
  const primary = current ? matches.length === 1 ? matches[0].id : current : assigned[0]?.id ?? ''
  return {
    ...form,
    Maßnahme: {
      ...fields,
      'Coach auswählen': primary,
      'Zweiter Coach (optional)': fields['Zweiter Coach (optional)'] ?? assigned[1]?.id ?? '',
    },
  }
}

async function upsertTbFormDaten(
  coachingId: string,
  values: Record<string, unknown>,
  identity: WriteIdentity = captureWriteIdentity(),
): Promise<void> {
  await invalidateFilledTb(coachingId, { strict: true }, identity)
  await patchDocData(coachingId, TB_EMPTY, { form: values }, undefined, identity)
}

export async function saveTbDraft(coachingId: string, values: FormValues, identity: WriteIdentity = captureWriteIdentity()): Promise<void> {
  await upsertTbFormDaten(coachingId, values, identity)
}

export async function saveTbForm(
  coachingId: string,
  values: Record<string, unknown>,
  identity: WriteIdentity = captureWriteIdentity(),
): Promise<void> {
  await upsertTbFormDaten(coachingId, values, identity)
  await saveTeilnahmeDates(coachingId, values, identity)
  await syncCoachingMassnahme(coachingId, values, identity)
  await syncCoachingCoach(coachingId, values, identity)
}

export async function generateFilledTb(
  coachingId: string,
  formData: FormValues,
  identity: WriteIdentity = captureWriteIdentity(),
): Promise<Result<void>> {
  try {
    const blank = await getDocBucketAddress(coachingId, TB_EMPTY)
    assertWriteIdentity(identity)
    if (!blank) {
      return technical("Keine hochgeladene Bestätigung gefunden. Bitte zuerst die leere Bestätigung hochladen.")
    }
    await saveTbForm(coachingId, formData, identity)
    const filled = await fillTb(coachingId, identity)
    assertWriteIdentity(identity)
    if (!filled.ok) return filled
    await setAufgabe(coachingId, 'tbPruefen', identity)
    return ok(undefined)
  } catch (error) {
    assertWriteIdentity(identity)
    console.error("Trägerbestätigung konnte nicht erstellt werden:", error)
    return technical("Trägerbestätigung konnte nicht erstellt werden. " + (error instanceof Error ? error.message : String(error)))
  }
}

export async function confirmTb(coachingId: string, identity: WriteIdentity = captureWriteIdentity()): Promise<AdvanceResult> {
  return setAufgabe(coachingId, 'vertragErstellen', identity)
}

function massnahmeLabelFrom(form: Record<string, unknown>): string {
  const fields = (form['Maßnahme'] ?? {}) as Record<string, unknown>
  return String(fields['Bezeichnung der Maßnahme'] ?? '').trim()
}

async function syncCoachingMassnahme(coachingId: string, form: Record<string, unknown>, identity: WriteIdentity): Promise<void> {
  const massnahme = await getMassnahmeTbFields(massnahmeLabelFrom(form))
  assertWriteIdentity(identity)
  if (!massnahme) throw new Error("Bitte wählen Sie eine gültige Maßnahme aus.")
  await apiPatch('/coaching/{id}', { id: coachingId }, { massnahme_id: massnahme.id }, identity)
}

async function syncCoachingCoach(coachingId: string, form: Record<string, unknown>, identity: WriteIdentity): Promise<void> {
  const fields = (form['Maßnahme'] ?? {}) as Record<string, unknown>
  const coaches = await listCoaches()
  assertWriteIdentity(identity)
  const selected = String(fields['Coach auswählen'] ?? '')
  const matches = coaches.filter((coach) => coach.id === selected || coach.name === selected)
  if (matches.length !== 1) throw new Error("Bitte wählen Sie einen eindeutigen Coach aus.")
  const second = String(fields['Zweiter Coach (optional)'] ?? '')
  if (second && (!coaches.some((coach) => coach.id === second) || second === matches[0].id)) {
    throw new Error("Bitte wählen Sie zwei unterschiedliche Coaches aus.")
  }
  await patchCoaching(coachingId, { coach_id: matches[0].id, second_coach_id: second || null }, identity)
}

async function saveTeilnahmeDates(
  coachingId: string,
  formData: Record<string, unknown>,
  identity: WriteIdentity,
): Promise<void> {
  const beginn = isoDate(getAt(formData, TB_BEGINN))
  const ende = isoDate(getAt(formData, TB_ENDE))
  if (!beginn && !ende) return
  const teilnahme = await apiFirst('/teilnahme', { coaching_id: coachingId, limit: 1 })
  assertWriteIdentity(identity)
  if (!teilnahme) throw new Error("Die Teilnahme ist nicht verfügbar.")
  await apiPatch(
    '/teilnahme/{coaching_id}/{teilnehmende_id}',
    { coaching_id: coachingId, teilnehmende_id: teilnahme.teilnehmende_id },
    { beginn: beginn ?? null, ende: ende ?? null },
    identity,
  )
}

function isoDate(value: unknown): string | null {
  return typeof value === 'string' && /\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null
}
